import Config from "../config.json";

const STASH_STORAGE_KEY = "pajoohyar_successful_login_payload";
const POST_LOGIN_REDIRECT_STORAGE_KEY = "pajoohyar_post_login_redirect";
const PROFILE_STORAGE_KEY = "inoor_profile_data";
const STORAGE_KEYS = {
  key: "key",
  userId: "userId",
  idToken: "id_token",
  guid: "guid",
  accessToken: "pajoohyar_api_access_token",
  expiresAt: "pajoohyar_api_expires_at",
};

export const SUCCESSFUL_LOGIN_EVENT = "pajoohyar-successful-login-sync";

export const normalizeProfileImageUrl = value => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  if (!normalized || /^(null|NuLL)(\.jpg)?$/i.test(normalized)) return null;
  return normalized;
};

const isBrowser = () => typeof window !== "undefined";

const getLocalStorage = () => (isBrowser() ? window.localStorage : null);
const getSessionStorage = () => (isBrowser() ? window.sessionStorage : null);

const normalizeReturnPath = value => {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized.startsWith("/")) return null;
  if (normalized.startsWith("//")) return null;
  return normalized;
};

const emitSuccessfulLoginEvent = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(SUCCESSFUL_LOGIN_EVENT));
};

/**
 * Stores the successful login payload in local storage for later reuse.
 * @param {{key?: string, userId?: string | number, id_token?: string, idToken?: string, guid?: string, access_token?: string, accessToken?: string, expires_at?: string | number, expiresAt?: string | number}} payload
 */
export const storeSuccessfulLoginData = payload => {
  if (!payload) return;

  const storage = getLocalStorage();
  if (!storage) return;

  const {
    key, userId, id_token, idToken, guid,
    access_token, accessToken, expires_at, expiresAt,
  } = payload;

  if (key) storage.setItem(STORAGE_KEYS.key, key);
  if (userId !== undefined && userId !== null)
    storage.setItem(STORAGE_KEYS.userId, String(userId));
  else storage.removeItem(STORAGE_KEYS.userId);

  const rawIdToken = idToken || id_token;
  const normalizedIdToken =
    rawIdToken !== undefined && rawIdToken !== null
      ? String(rawIdToken).trim()
      : "";
  if (normalizedIdToken)
    storage.setItem(STORAGE_KEYS.idToken, normalizedIdToken);
  else storage.removeItem(STORAGE_KEYS.idToken);

  const normalizedGuid =
    guid !== undefined && guid !== null ? String(guid).trim() : "";
  if (normalizedGuid) storage.setItem(STORAGE_KEYS.guid, normalizedGuid);
  else storage.removeItem(STORAGE_KEYS.guid);

  const apiToken = accessToken || access_token;
  const apiExpiry = expiresAt || expires_at;
  if (apiToken) storage.setItem(STORAGE_KEYS.accessToken, String(apiToken));
  else storage.removeItem(STORAGE_KEYS.accessToken);
  if (apiExpiry) storage.setItem(STORAGE_KEYS.expiresAt, String(apiExpiry));
  else storage.removeItem(STORAGE_KEYS.expiresAt);

  [
    "pajoohyar_zotero_key",
    "pajoohyar_zotero_userId",
    "pajoohyar_zotero_username",
    "pajoohyar_zotero_displayName",
    "pajoohyar_zotero_name",
    "pajoohyar_zotero_fullName",
    "user_full_name",
    "user_full_path_picture",
    "preferred_username",
  ].forEach(legacyKey => storage.removeItem(legacyKey));

  emitSuccessfulLoginEvent();
};

/**
 * Persists the complete profile payload returned from the identity provider.
 * The payload is stored before it is used anywhere else to avoid mutations.
 * @param {Record<string, string>} profileData
 */
export const storeProfileData = profileData => {
  if (!profileData || typeof profileData !== "object") return;

  const storage = getLocalStorage();
  if (!storage) return;

  const sanitized = Object.keys(profileData).reduce((acc, key) => {
    const value = profileData[key];
    if (value === undefined || value === null) return acc;
    acc[key] = String(value);
    return acc;
  }, {});

  if (Object.keys(sanitized).length === 0) return;

  try {
    storage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(sanitized));
  } catch (error) {
    console.error("Failed to persist profile data", error);
  }
};

/**
 * Temporarily stashes the successful login payload in session storage so it
 * can be consumed later during the auth callback flow.
 * @param {{key?: string, userId?: string | number, guid?: string}} payload
 */
export const stashSuccessfulLoginPayload = payload => {
  const storage = getSessionStorage();
  if (!storage || !payload) return;

  try {
    storage.setItem(STASH_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to stash successful login payload", error);
  }
};

/**
 * Retrieves and clears any previously stashed successful login payload.
 * @returns {{key?: string, userId?: string | number, guid?: string} | null}
 */
export const consumeSuccessfulLoginPayload = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const raw = storage.getItem(STASH_STORAGE_KEY);
  if (!raw) return null;

  storage.removeItem(STASH_STORAGE_KEY);

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (error) {
    console.error("Failed to parse stashed successful login payload", error);
    return null;
  }
};

/**
 * Stores a same-origin post-login redirect path in session storage.
 * @param {string} path
 */
export const setPostLoginRedirect = path => {
  const storage = getSessionStorage();
  const normalized = normalizeReturnPath(path);
  if (!storage || !normalized) return;

  try {
    storage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, normalized);
  } catch (error) {
    console.error("Failed to store post-login redirect", error);
  }
};

/**
 * Reads the stored post-login redirect path.
 * @returns {string | null}
 */
export const getPostLoginRedirect = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const raw = storage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  return normalizeReturnPath(raw);
};

/**
 * Reads and clears the stored post-login redirect path.
 * @returns {string | null}
 */
export const consumePostLoginRedirect = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const redirectPath = normalizeReturnPath(
    storage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY),
  );
  storage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  return redirectPath;
};

/**
 * Builds the login URL for the Inoor service with the correct callback.
 * @param {string | null} returnTo
 * @returns {string} The absolute login URL.
 */
export const getSuccessfulLoginUrl = (returnTo = null) => {
  if (returnTo) setPostLoginRedirect(returnTo);

  const origin = isBrowser() && window.location ? window.location.origin : "";
  const normalizedOrigin = origin.endsWith("/") ? origin.slice(0, -1) : origin;
  const callback = normalizedOrigin
    ? `${normalizedOrigin}/successful_login`
    : "/successful_login";
  const data = JSON.stringify({ callback });
  const loginUrl = process.env.REACT_APP_LOGIN_URL || Config.inoor_url;
  const separator = loginUrl.includes("?") ? "&" : "?";
  return `${loginUrl}${separator}context=web-library&data=${encodeURIComponent(
    data,
  )}`;
};

/**
 * Reads the stored successful login data from localStorage.
 * @returns {{key?: string, userId?: string, idToken?: string, guid?: string, accessToken?: string, expiresAt?: string} | null}
 */
export const getStoredSuccessfulLoginData = () => {
  const storage = getLocalStorage();
  if (!storage) return null;

  const key = storage.getItem(STORAGE_KEYS.key);
  const userId = storage.getItem(STORAGE_KEYS.userId);

  const accessToken = storage.getItem(STORAGE_KEYS.accessToken) || undefined;
  if (!key && !userId && !accessToken) return null;

  const idToken = storage.getItem(STORAGE_KEYS.idToken) || undefined;
  const guid = storage.getItem(STORAGE_KEYS.guid) || undefined;
  const expiresAt = storage.getItem(STORAGE_KEYS.expiresAt) || undefined;

  return {
    key: key || undefined,
    userId: userId || undefined,
    idToken,
    guid,
    accessToken,
    expiresAt,
  };
};

/**
 * Reads the last stored profile information (if any) from localStorage.
 * @returns {Record<string, string> | null}
 */
export const getStoredProfileData = () => {
  const storage = getLocalStorage();
  if (!storage) return null;

  const raw = storage.getItem(PROFILE_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch (error) {
    console.error("Failed to parse stored profile data", error);
    return null;
  }
};

/**
 * Clears all stored successful login data.
 */
export const clearSuccessfulLoginData = () => {
  const storage = getLocalStorage();
  if (storage) {
    Object.values(STORAGE_KEYS).forEach(key => storage.removeItem(key));
    storage.removeItem(PROFILE_STORAGE_KEY);

    [
      "pajoohyar_zotero_key",
      "pajoohyar_zotero_userId",
      "pajoohyar_zotero_username",
      "pajoohyar_zotero_displayName",
      "pajoohyar_zotero_name",
      "pajoohyar_zotero_fullName",
    ].forEach(legacyKey => storage.removeItem(legacyKey));
  }
  const sessionStorageRef = getSessionStorage();
  if (sessionStorageRef) {
    sessionStorageRef.removeItem(STASH_STORAGE_KEY);
    sessionStorageRef.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  }
  emitSuccessfulLoginEvent();
};

export default getSuccessfulLoginUrl;
